/**
 * Plain-text export of the job workspace. The page has no document file of its own,
 * so Export downloads the same fields the workspace is showing.
 */

/**
 * @param {string} jobNumber
 * @returns {string}
 */
export function workspaceExportFileName(jobNumber) {
  const safe = String(jobNumber || "job")
    .trim()
    .replace(/[^\w.-]+/g, "-");
  return `${safe || "job"}.txt`;
}

/**
 * @param {object} view
 * @returns {string}
 */
export function buildWorkspaceExportText(view) {
  const record = view || {};
  const lines = [
    record.jobNumber || "Job",
    `Status: ${record.statusLabel || ""}`,
    record.priorityLabel || "",
    record.loadLabel || "",
    record.companyLabel || "",
    record.active ? "Active" : "Inactive",
    "",
    "Summary",
  ];

  for (const item of record.summary || []) {
    lines.push(`${item.label}: ${item.value}${item.detail ? ` (${item.detail})` : ""}`);
  }

  lines.push("", record.legsTitle || "Loads & legs");
  if (!record.legs?.length) {
    lines.push("No legs on this job.");
  }
  for (const leg of record.legs || []) {
    lines.push(
      [leg.label, leg.loadNumber, leg.route, leg.driver, leg.status].filter(Boolean).join(" | ")
    );
  }
  if (record.unresolvedCopy) lines.push(record.unresolvedCopy);

  lines.push("", "Dispatch note", record.dispatchNote || "");
  for (const item of record.schedule || []) {
    lines.push(`${item.label}: ${item.value}`);
  }

  lines.push("", "People");
  for (const person of record.people || []) {
    lines.push(
      `${person.label}: ${person.title}${person.detail ? ` (${person.detail})` : ""}`
    );
  }

  lines.push("", "Financial summary", record.finance?.total || "");
  lines.push(record.finance?.currencyLabel || "");
  for (const amount of record.finance?.amounts || []) {
    lines.push(`${amount.label}: ${amount.value}`);
  }
  lines.push("", `Approval: ${record.approval?.label || ""}`);
  if (record.approval?.reviewedLabel) lines.push(`Reviewed: ${record.approval.reviewedLabel}`);
  if (record.approval?.note) lines.push(record.approval.note);

  return lines.join("\n");
}

/**
 * Download the workspace export in the browser.
 * @param {object} view
 * @returns {void}
 */
export function downloadWorkspaceExport(view) {
  if (typeof document === "undefined") return;
  const text = buildWorkspaceExportText(view);
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = workspaceExportFileName(view?.jobNumber);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
